import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layers } from "lucide-react";
import { usePortalAuth } from "@/components/portal/PortalAuth";
import { Card, EmptyState, Spinner, StatusBadge, gbp2 } from "@/components/portal/shared";

const ACTIVE = ["Deposit Paid", "Order Confirmed", "Manufacturing"];

export default function MaterialsPage() {
  const { api } = usePortalAuth();
  const [orders, setOrders] = useState(null);

  const load = () => api.get("/orders").then((r) => setOrders(r.data.orders)).catch(() => setOrders([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  if (orders === null) return <Spinner />;

  const active = orders.filter((o) => ACTIVE.includes(o.status));

  const advance = async (o) => {
    const next = o.status === "Deposit Paid" ? "Order Confirmed" : o.status === "Order Confirmed" ? "Manufacturing" : null;
    if (!next) return;
    await api.patch(`/orders/${o.id}`, { status: next });
    await load();
  };

  return (
    <div data-testid="materials-page" className="space-y-5">
      <p className="text-sm text-slate-500">{active.length} order{active.length === 1 ? "" : "s"} in production — materials and made-to-measure items to order or make.</p>
      {active.length === 0 ? (
        <EmptyState icon={Layers}>Nothing in production right now. Orders appear here once a deposit is paid.</EmptyState>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {active.map((o) => (
            <Card key={o.id} testid={`materials-card-${o.id}`} title={`${o.ref} — ${o.customer}`} action={<StatusBadge s={o.status} />}>
              <table className="w-full text-xs mb-4">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-warm-200">
                    <th className="py-2 pr-2">Item</th>
                    <th className="py-2 pr-2 w-12">Qty</th>
                    <th className="py-2 w-20 text-right">Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-warm-200/60">
                  {o.items.map((i, x) => (
                    <tr key={x}>
                      <td className="py-2 pr-2 text-slate-700">{i.description}</td>
                      <td className="py-2 pr-2 text-slate-500">{i.qty}</td>
                      <td className="py-2 text-right font-medium text-slate-800">{gbp2(i.price * i.qty)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center justify-between gap-3">
                <Link to={`/portal/customers/${o.lead_id}?tab=orders`} className="text-xs font-semibold text-navy-900 hover:underline">View customer</Link>
                {o.status !== "Manufacturing" && (
                  <button type="button" data-testid={`materials-advance-${o.id}`} onClick={() => advance(o)} className="inline-flex items-center gap-1 rounded-full bg-navy-900 px-3.5 py-1.5 text-[11px] font-semibold text-white hover:bg-navy-800">
                    Move to {o.status === "Deposit Paid" ? "Order Confirmed" : "Manufacturing"}
                  </button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
